/**
 * Audit-log domain types (feature `19`). An audit entry records who did what,
 * to which entity, when, and from→to — for membership, billing, mailbox /
 * template / integration deletes, and user-initiated warmup changes. Automatic
 * (system) warmup transitions are NEVER audited; they surface as notifications
 * only.
 */

/** The coarse module an audit entry belongs to — the primary filter axis. */
export enum AuditModule {
  MEMBERSHIP = 'MEMBERSHIP',
  BILLING = 'BILLING',
  WARMUP = 'WARMUP',
  /** Mailbox deletes, including a Google/Microsoft workspace connection's. */
  MAILBOXES = 'MAILBOXES',
  /** Template-library collection and template deletes. */
  TEMPLATES = 'TEMPLATES',
  /** API keys and the Saleshandy / partner integrations. */
  INTEGRATIONS = 'INTEGRATIONS',
}

/** The specific action an audit entry records (`<domain>.<verb>`). */
export enum AuditAction {
  // Membership (settings + auth)
  WorkspaceCreated = 'workspace.created',
  WorkspaceDeleted = 'workspace.deleted',
  MemberInvited = 'member.invited',
  MemberInviteRevoked = 'member.invite_revoked',
  MemberJoined = 'member.joined',
  MemberRemoved = 'member.removed',
  MemberRoleChanged = 'member.role_changed',
  /**
   * An admin adding an EXISTING org member directly to a workspace (Settings →
   * People → "Add to a workspace"), no invite/accept round trip. Distinct from
   * `MemberJoined`, which is a self-service join (invite/link acceptance,
   * profile completion) and records the JOINER as actor — here the acting
   * admin is the actor and the added user is the entity.
   */
  MemberAdded = 'member.added',
  OrgMemberRemoved = 'org.member_removed',
  OrgRoleChanged = 'org.role_changed',
  OrgOwnershipTransferred = 'org.ownership_transferred',
  OrgQuotaChanged = 'org.quota_changed',
  OrgTimezoneChanged = 'org.timezone_changed',
  // Billing. `subscription.changed` is written for a user's plan change AND,
  // with no actor ("System"), for an automatic one — a Paddle webhook, the
  // expiry sweep, a Saleshandy sync, signup — but only when the plan or status
  // actually moved (or the org's first subscription was created); a renewal
  // that changes neither writes nothing. Support tools (APUS, the internal
  // assign) are never audited here.
  SubscriptionChanged = 'subscription.changed',
  TrialExtended = 'trial.extended',
  // Billing lifecycle requests: the OWNER's REQUEST is audited at request time
  // (recorded only after Paddle accepts it); the resulting state change lands
  // later through the webhook as a System `subscription.changed`.
  SubscriptionUpgradeRequested = 'subscription.upgrade_requested',
  SubscriptionCancelRequested = 'subscription.cancel_requested',
  SubscriptionPauseRequested = 'subscription.pause_requested',
  SubscriptionResumeRequested = 'subscription.resume_requested',
  SubscriptionScheduledChangeRevoked = 'subscription.scheduled_change_revoked',
  DiscountRedeemed = 'discount.redeemed',
  // Warmup — user-initiated ONLY
  WarmupSettingsChanged = 'warmup.settings_changed',
  WarmupEnabled = 'warmup.enabled',
  WarmupPaused = 'warmup.paused',
  WarmupResumed = 'warmup.resumed',
  // Mailboxes. A mass delete (a workspace connection, the partner integration)
  // records its own entry PLUS one `mailbox.deleted` per mailbox it removed.
  MailboxDeleted = 'mailbox.deleted',
  WorkspaceConnectionDeleted = 'workspace_connection.deleted',
  // Membership — the shareable invite link (distinct from a per-email invite).
  MemberInviteLinkRevoked = 'member.invite_link_revoked',
  // Templates
  TemplateCollectionDeleted = 'template.collection_deleted',
  TemplateDeleted = 'template.deleted',
  // Integrations
  ApiKeyRevoked = 'api_key.revoked',
  SaleshandyUnlinked = 'saleshandy.unlinked',
  PartnerRemoved = 'partner.removed',
}

/**
 * The kind of entity an audit entry points at (paired with `entityId`) — the
 * secondary filter axis after `module`/`action`.
 *
 * The values are the exact camelCase strings already persisted in
 * `auditLogs.entityType` (a plain `varchar(64)`, not a MySQL enum). Do NOT
 * restyle them to match `AuditModule`'s SCREAMING_CASE — every historical row
 * would stop matching the `entityType` filter.
 */
export enum AuditEntityType {
  Workspace = 'workspace',
  WorkspaceInvite = 'workspaceInvite',
  WorkspaceMember = 'workspaceMember',
  WorkspaceQuota = 'workspaceQuota',
  Organization = 'organization',
  OrganizationMember = 'organizationMember',
  Subscription = 'subscription',
  EmailAccount = 'emailAccount',
  WorkspaceInviteLink = 'workspaceInviteLink',
  /** A Google Workspace / Microsoft tenant connected for bulk mailbox import. */
  WorkspaceConnection = 'workspaceConnection',
  TemplateCollection = 'templateCollection',
  Template = 'template',
  ApiKey = 'apiKey',
  SaleshandyAccount = 'saleshandyAccount',
  PartnerIntegration = 'partnerIntegration',
}

/** Where an audit-worthy change originated — folded into `metadata.source`. */
export enum AuditSource {
  UI = 'ui',
  WEBHOOK = 'webhook',
  SYSTEM = 'system',
  /** Saleshandy's server calling our Saleshandy API — no TrulyInbox user acted. */
  SALESHANDY = 'saleshandy',
  /** An Apus support-impersonation session wrote this row. */
  APUS = 'apus',
}

/** `actorName` for an entry written on Saleshandy's call (no TrulyInbox user). */
export const AUDIT_ACTOR_NAME_SALESHANDY = 'Saleshandy';

/** `actorName` for an entry written during an Apus support-impersonation session. */
export const AUDIT_ACTOR_NAME_APUS = 'Saleshandy Support';

/**
 * The payload passed to `AuditService.record(...)`. `before`/`after` carry only
 * the changed fields (not the whole entity). Actor fields are null for
 * system/webhook-driven changes.
 */
export interface AuditLogEntry {
  orgId: number;
  workspaceId?: number | null;
  actorUserId?: number | null;
  /**
   * Display name for an actor that is not a user (e.g.
   * `AUDIT_ACTOR_NAME_SALESHANDY`). Ignored when `actorUserId` is set — a
   * user's name is always resolved from the `user` table. Null/absent with no
   * `actorUserId` renders as "System".
   */
  actorName?: string | null;
  actorRole?: string | null;
  /** True only for a row written during an Apus support-impersonation session. */
  isApus?: boolean;
  module: AuditModule;
  action: AuditAction;
  entityType: AuditEntityType;
  entityId?: string | null;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
}
